import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  booleanAttribute,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { BlobService, StorageService } from '@insite-store360/services';
import { from, page, to } from '@insite-store360/shared';
import {
  LEVEL_3_GROUP_ROLES,
  PROMOTER_ROLE_ID,
  SALES_LEVEL_GROUP_ROLES,
} from 'library/api-common/src/lib/utils/appliationConstants';
import { ToastrService } from 'ngx-toastr';
import { AvatarComponent } from '../avatar/avatar.component';
import { DataNotFoundComponent } from '../data-not-found/data-not-found.component';
import { ImagemodalComponent } from '../imagemodal/imagemodal.component';
import { PaginatorComponent } from '../paginator/paginator.component';
import { QrCodeDialogComponent } from '../qr-code-dialog/qr-code-dialog.component';
import { copyQrImageToClipboard, generateQrDataUrl } from '../qr-code-dialog/qr-code.util';
import { TableSkeletonComponent } from '../skeleton/table-skeleton/table-skeleton.component';
export interface ColumnTemplate {
  [key: string]: TemplateRef<any>;
}

@Component({
  selector: 'lib-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatRadioModule,
    MatTableModule,
    MatIconModule,
    PaginatorComponent,
    MatSlideToggleModule,
    MatTooltipModule,
    DataNotFoundComponent,
    MatCheckboxModule,
    TableSkeletonComponent,
    MatChipsModule,
    MatSortModule,
    AvatarComponent,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
})
export class TableComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('tableWrapper') tableWrapper!: ElementRef<HTMLDivElement>;
  @ViewChild(MatSort) matSort!: MatSort;
  @ViewChild('horizontalTrack') horizontalTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('horizontalThumb') horizontalThumb!: ElementRef<HTMLDivElement>;
  @ViewChild('verticalTrack') verticalTrack!: ElementRef<HTMLDivElement>;
  @ViewChild('verticalThumb') verticalThumb!: ElementRef<HTMLDivElement>;

  @Input() customTemplates: ColumnTemplate = {};
  @Input() pageIndex: number | undefined;
  @Input() pageSize: number | undefined;
  @Input() totalCount: number | undefined;
  @Input() customizedPagiantor: boolean = false;
  @Input() tableHeight: string = 'auto';
  @Input() tableWidth: string = '100%';
  @Input() actionHeader: boolean = false;
  // @Input({ transform: booleanAttribute }) stickyHeader: boolean = true;
  @Input({ transform: booleanAttribute }) fixedHeader: boolean = false;
  public tableSkeleton: boolean = false;

  showHorizontalScrollbar: boolean = false;
  showVerticalScrollbar: boolean = false;
  horizontalThumbLeft: number = 0;
  horizontalThumbWidth: number = 0;
  verticalThumbTop: number = 0;
  verticalThumbHeight: number = 0;
  private isDraggingHorizontal: boolean = false;
  private isDraggingVertical: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private dragStartScrollLeft: number = 0;
  private dragStartScrollTop: number = 0;
  private resizeObserver?: ResizeObserver;
  @Input() page: number[] = page;
  @Input() from: number = from;
  @Input() editRow: boolean = true;
  @Input() deleteRow: boolean = true;
  @Input() alwaysShowAction: boolean = false;
  @Input() to: number = to;
  @Input({ transform: booleanAttribute }) isBlock: boolean = false;
  @Input() isLoading: boolean = false;
  @Output() pageSizeChange = new EventEmitter<any>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() decrementValue = new EventEmitter<any>();
  @Output() incrementValue = new EventEmitter<any>();
  @Output() overIncrementValue = new EventEmitter<any>();
  @Output() overDecrementValue = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() aegSurveyView = new EventEmitter<any>();
  @Output() snagView = new EventEmitter<any>();
  @Output() snagResolve = new EventEmitter<any>();
  @Output() snagReopen = new EventEmitter<any>();
  @Output() isActiveChange = new EventEmitter<{
    id: number;
    status: boolean;
  }>();

  @Output() leaveApprovalChange = new EventEmitter<{
    id: number;
    isApproval: boolean;
  }>();

  @Output() pmAccessChange = new EventEmitter<{
    id: number;
    hasPmAccess: boolean;
  }>();

  @Output() isInvoiceRequiredChange = new EventEmitter<{
    id: number;
    isInvoiceRequired: boolean;
  }>();

  @Output() isEnterPriceChange = new EventEmitter<{
    id: number;
    isEnterPrice: boolean;
  }>();

  @Output() isTestChange = new EventEmitter<{
    id: number;
    isTest: boolean;
  }>();

  @Output() dynamicStatusChange = new EventEmitter<{
    id: number;
    fieldName: string;
    value: boolean;
  }>();

  @Input() grandTotalHeader: string[] = [];
  @Input() grandTotalValue: any;
  @Output() categoryGroupTargetRow = new EventEmitter<any>();

  @Output() addUserStore = new EventEmitter<any>();
  @Output() addSurveyMap = new EventEmitter<any>();
  @Output() resetPassword = new EventEmitter<any>();
  @Output() resetStoreLocation = new EventEmitter<any>();
  // custom paginator

  @Input() dataSource: any;
  @Input() displayedColumns: { key: string; id: string }[] = [];
  @Output() selectRowValue = new EventEmitter<any>();
  @Output() selectedUsers = new EventEmitter<any[]>();
  @Output() selectRowClick = new EventEmitter<any>();
  @Output() cancelTrainingList = new EventEmitter<any>();
  selectedRow: any = {};
  selectedValue!: {};
  currentCheckedValue: any = null;

  get columnsList(): string[] {
    return this.displayedColumns?.map((c) => c.id) ?? [];
  }

  /** When column count is low, spread columns across the full table width. */
  get isFewColumns(): boolean {
    return this.columnsList.length > 0 && this.columnsList.length <= 8;
  }
  isHidden: boolean = false;
  public selection = new SelectionModel<any>(true, []);
  public userId: number;
  public userRole: string;
  public levelThreeGroupRoles: any = LEVEL_3_GROUP_ROLES;
  readonly leaveApprovalHiddenRoleIds: number[] = [
    PROMOTER_ROLE_ID,
    ...SALES_LEVEL_GROUP_ROLES,
  ];

  modalImages: string[] = [];
  checked = true;
  disabled = true;
  private readonly invoiceUrlCache = new Map<string, string>();
  private readonly pendingBlobPreviewUrls = new Set<string>();
  private static readonly BLOB_IMAGE_COLUMN_IDS = new Set([
    'beforeImage1',
    'beforeImage2',
    'afterImage1',
    'afterImage2',
    'profile',
  ]);

  private static readonly CLAMPED_LIST_COLUMN_IDS = new Set([
    'categoryName',
    'allSubCategories',
    'subCategoryName',
    'tagModelName',
  ]);


  constructor(
    private ren: Renderer2,
    private storage: StorageService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private blobService: BlobService,
    private cdr: ChangeDetectorRef,
  ) {
    this.userId = Number(this.storage.fetchdata('id'));
    const storedRole = this.storage.fetchdata('role');
    this.userRole = storedRole ? JSON.parse(storedRole) : '';
  }

  ngAfterViewInit(): void {
    this.connectSort();
    if (this.tableWrapper) {
      this.updateScrollbars();
      this.resizeObserver = new ResizeObserver(() => {
        this.updateScrollbars();
      });
      this.resizeObserver.observe(this.tableWrapper.nativeElement);
    }
  }

  onSortChange(sort: Sort): void {
    this.sortChange.emit(sort);
    this.cdr.detectChanges();
  }

  private connectSort(): void {
    if (!this.dataSource || !this.matSort) {
      return;
    }
    this.dataSource.sort = this.matSort;
    if (this.dataSource instanceof MatTableDataSource) {
      this.dataSource.sortingDataAccessor = (item, property) => {
        if (property === 'sno') {
          return null;
        }
        const value = item?.[property];
        if (value === null || value === undefined || value === '') {
          return '';
        }
        return typeof value === 'string' ? value.toLowerCase() : value;
      };
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    this.removeDragListeners();
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize(): void {
    this.updateScrollbars();
  }

  onTableScroll(event: Event): void {
    if (!this.isDraggingHorizontal && !this.isDraggingVertical) {
      this.updateScrollbarPositions();
    }
  }

  updateScrollbars(): void {
    if (!this.tableWrapper) return;

    const wrapper = this.tableWrapper.nativeElement;
    const scrollWidth = wrapper.scrollWidth;
    const scrollHeight = wrapper.scrollHeight;
    const clientWidth = wrapper.clientWidth;
    const clientHeight = wrapper.clientHeight;

    if (scrollWidth > clientWidth) {
      this.showHorizontalScrollbar = true;
      const trackWidth = wrapper.clientWidth;
      const thumbWidth = Math.max(30, (clientWidth / scrollWidth) * trackWidth);
      this.horizontalThumbWidth = thumbWidth;
    } else {
      this.showHorizontalScrollbar = false;
    }

    if (scrollHeight > clientHeight) {
      this.showVerticalScrollbar = true;
      const trackHeight = wrapper.clientHeight;
      const thumbHeight = Math.max(30, (clientHeight / scrollHeight) * trackHeight);
      this.verticalThumbHeight = thumbHeight;
    } else {
      this.showVerticalScrollbar = false;
    }

    this.updateScrollbarPositions();
  }

  updateScrollbarPositions(): void {
    if (!this.tableWrapper) return;

    const wrapper = this.tableWrapper.nativeElement;
    const scrollLeft = wrapper.scrollLeft;
    const scrollTop = wrapper.scrollTop;
    const scrollWidth = wrapper.scrollWidth;
    const scrollHeight = wrapper.scrollHeight;
    const clientWidth = wrapper.clientWidth;
    const clientHeight = wrapper.clientHeight;

    if (this.showHorizontalScrollbar) {
      const trackWidth = wrapper.clientWidth;
      const maxThumbLeft = trackWidth - this.horizontalThumbWidth;
      const scrollRatio = scrollLeft / (scrollWidth - clientWidth);
      this.horizontalThumbLeft = Math.min(maxThumbLeft, scrollRatio * maxThumbLeft);
    }

    if (this.showVerticalScrollbar) {
      const trackHeight = wrapper.clientHeight;
      const maxThumbTop = trackHeight - this.verticalThumbHeight;
      const scrollRatio = scrollTop / (scrollHeight - clientHeight);
      this.verticalThumbTop = Math.min(maxThumbTop, scrollRatio * maxThumbTop);
    }
  }

  onHorizontalScrollbarMouseDown(event: MouseEvent): void {
    if (!this.tableWrapper || !this.horizontalTrack) return;

    event.preventDefault();
    event.stopPropagation();

    this.isDraggingHorizontal = true;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.tableWrapper.nativeElement.scrollLeft;

    document.addEventListener('mousemove', this.onHorizontalMouseMove);
    document.addEventListener('mouseup', this.onHorizontalMouseUp);
  }

  onVerticalScrollbarMouseDown(event: MouseEvent): void {
    if (!this.tableWrapper || !this.verticalTrack) return;

    event.preventDefault();
    event.stopPropagation();

    this.isDraggingVertical = true;
    this.dragStartY = event.clientY;
    this.dragStartScrollTop = this.tableWrapper.nativeElement.scrollTop;

    document.addEventListener('mousemove', this.onVerticalMouseMove);
    document.addEventListener('mouseup', this.onVerticalMouseUp);
  }

  private onHorizontalMouseMove = (event: MouseEvent): void => {
    if (!this.isDraggingHorizontal || !this.tableWrapper || !this.horizontalTrack) return;

    const track = this.horizontalTrack.nativeElement;
    const trackWidth = track.clientWidth;
    const trackRect = track.getBoundingClientRect();
    const relativeX = event.clientX - trackRect.left;
    const scrollWidth = this.tableWrapper.nativeElement.scrollWidth;
    const clientWidth = this.tableWrapper.nativeElement.clientWidth;
    const maxScrollLeft = scrollWidth - clientWidth;

    const scrollRatio = Math.max(0, Math.min(1, relativeX / trackWidth));
    const newScrollLeft = scrollRatio * maxScrollLeft;

    this.tableWrapper.nativeElement.scrollLeft = newScrollLeft;
    this.updateScrollbarPositions();
  };

  private onHorizontalMouseUp = (): void => {
    this.isDraggingHorizontal = false;
    document.removeEventListener('mousemove', this.onHorizontalMouseMove);
    document.removeEventListener('mouseup', this.onHorizontalMouseUp);
  };

  private onVerticalMouseMove = (event: MouseEvent): void => {
    if (!this.isDraggingVertical || !this.tableWrapper || !this.verticalTrack) return;

    const track = this.verticalTrack.nativeElement;
    const trackHeight = track.clientHeight;
    const trackRect = track.getBoundingClientRect();
    const relativeY = event.clientY - trackRect.top;
    const scrollHeight = this.tableWrapper.nativeElement.scrollHeight;
    const clientHeight = this.tableWrapper.nativeElement.clientHeight;
    const maxScrollTop = scrollHeight - clientHeight;

    const scrollRatio = Math.max(0, Math.min(1, relativeY / trackHeight));
    const newScrollTop = scrollRatio * maxScrollTop;

    this.tableWrapper.nativeElement.scrollTop = newScrollTop;
    this.updateScrollbarPositions();
  };

  private onVerticalMouseUp = (): void => {
    this.isDraggingVertical = false;
    document.removeEventListener('mousemove', this.onVerticalMouseMove);
    document.removeEventListener('mouseup', this.onVerticalMouseUp);
  };

  private removeDragListeners(): void {
    document.removeEventListener('mousemove', this.onHorizontalMouseMove);
    document.removeEventListener('mouseup', this.onHorizontalMouseUp);
    document.removeEventListener('mousemove', this.onVerticalMouseMove);
    document.removeEventListener('mouseup', this.onVerticalMouseUp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['displayedColumns']) {
      const { currentValue } = changes['displayedColumns'];
      if (currentValue) {
        this.displayedColumns = currentValue;
      }
    }
    if (changes['dataSource']) {
      const { currentValue } = changes['dataSource'];
      this.tableSkeleton = !currentValue || currentValue.length === 0;
      if (this.tableWrapper) {
        setTimeout(() => this.updateScrollbars(), 0);
      }
      const rows = this.dataSource?.data;
      if (Array.isArray(rows)) {
        rows.forEach((row: any) => {
          this.ensureInvoicePreviewUrl(row);
        });
        this.prefetchBlobImagePreviews(rows);
      }
      setTimeout(() => this.connectSort());
    }
    this.dataSource?.data?.forEach((row: any) => {
      if (row.sent) {
        this.selection.select(row)
      }
    })
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource?.data?.length || 0;
    return numSelected === numRows;
  }

  public toggleSelectAll(checked: boolean): void {
    if (checked) {
      this.dataSource?.data?.forEach((row: any) => this.selection.select(row));
    } else {
      this.selection.clear();
    }
    this.selectedUsers.emit(this.selection.selected);
  }


  radioSelected = (row: any) => {
    this.selectedValue = {};
    this.selectedValue = row;
    this.selectRowValue.emit(row);
  };
  public clearSelectedRows(): void {
    this.selection.clear(); // Clear all selected rows
    this.selectedUsers.emit([]); // Optional: Emit empty array to reset parent state
  }

  checkboxLabel(row?: Request): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${
      /* row.emp_id + */ 1
      }`;
  }
  callIt(row: any) {
    this.selectedRow = true;
    const selectedUsersArray = this.selection.selected; // Assuming `selection` holds the selected rows
    this.selectedUsers.emit(selectedUsersArray);
    // console.log('Getting the selected row', this.selection.selected);
  }

  checkBoxSelected(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) || row.sent ? 'deselect' : 'select'}row ${row.t
      }`;
  }

  checkState(el: any) {
    setTimeout(() => {
      if (this.currentCheckedValue && this.currentCheckedValue === el.value) {
        el.checked = false;
        this.ren.removeClass(el['_elementRef'].nativeElement, 'cdk-focused');
        this.ren.removeClass(
          el['_elementRef'].nativeElement,
          'cdk-program-focused'
        );
        this.currentCheckedValue = null;
        let row = null;
        this.radioSelected(row);
      } else {
        this.currentCheckedValue = el.value;
      }
    });
  }

  selectRow(event: any, value: any) {
    // If the click originated from a .store-link, do nothing
    if (event.target && event.target.classList && event.target.classList.contains('store-link')) {
      return;
    }
    event.stopPropagation();
    this.selectRowClick.emit(value);
  }
  editUser(element: any) {
    // console.log(element);
    this.edit.emit(element);
  }
  deleteUser(element: any) {
    // console.log(element);
    let userRoleData: string[] = element?.roles?.map((val: any) => val.name);
    if (element.id === this.userId && userRoleData?.includes(this.userRole)) {
      this.toastr.error('User cannot delete their own account');
    } else {
      this.delete.emit(element);
    }
  }

  // custom paginator
  pageSizeChangeTable(event: any) {
    this.pageSizeChange.emit(event);
  }
  decrementValueTable(event: any) {
    this.decrementValue.emit(event);
  }
  incrementValueTable(event: any) {
    this.incrementValue.emit(event);
  }
  overIncrementValueTable(event: any) {
    this.overIncrementValue.emit(event);
  }
  overDecrementValueTable() {
    this.overDecrementValue.emit(event);
  }
  toggleStatus(id: number, status: boolean) {
    this.isActiveChange.emit({
      id: id,
      status: status,
    });
  }

  toggleLeaveApproval(id: number, isApproval: boolean) {
    this.leaveApprovalChange.emit({
      id,
      isApproval,
    });
  }

  togglePmAccess(id: number, hasPmAccess: boolean) {
    this.pmAccessChange.emit({
      id,
      hasPmAccess,
    });
  }

  shouldShowLeaveApprovalToggle(element: any): boolean {
    const roleIds = element?.roleId;
    if (!roleIds || !Array.isArray(roleIds)) return false;
    return !roleIds.some((id: number) => this.leaveApprovalHiddenRoleIds.includes(id));
  }

  getTextColor(color: string) {
    if (color === 'Pending') {
      return '#FF8A00';
    } else if (color === 'Completed' || color === 'Sellable' || color === 'Submitted') {
      return '#519C66';
    } else if (color === 'Non-Sellable' || color === 'Non Sellable') {
      return '#E74C3C';
    } else if (color === 'Open') {
      return '#1890FF';
    } else if (color === 'In Progress') {
      return '#FF8A00';
    } else if (color === 'Resolved') {
      return '#519C66';
    } else if (color === 'Closed') {
      return '#6B7280';
    }
    return '#FF0000';
  }

  // Function to get the background color based on task status
  getBackgroundColor(color: string) {
    if (color === 'Pending') {
      return '#FF8A001A';
    } else if (color === 'Completed' || color === 'Sellable' || color === 'Submitted') {
      return '#E8F5E9';
    } else if (color === 'Non-Sellable' || color === 'Non Sellable') {
      return '#FFEBEE';
    } else if (color === 'Open') {
      return '#E3F2FD';
    } else if (color === 'In Progress') {
      return '#FFF3E0';
    } else if (color === 'Resolved') {
      return '#E8F5E9';
    } else if (color === 'Closed') {
      return '#F3F4F6';
    }
    return '#519C661A';
  }


  getColor(color: string) {
    if (color === 'Rejected') {
      return '#F44336';
    } else if (color === 'Announced') {
      return '#339933';
    } else if (color === 'Approved') {
      return '#339933';
    } else if (color === 'Pending') {
      return '#1890FF';
    } else {
      return '#2ECD6F';
    }
  }



  isAnArray(item: any) {
    if (typeof item === 'object' && Object.keys(item).length > 0) {
      return item?.name;
    } else if (Array.isArray(item) && item.length > 0) {
      return item;
    } else {
      return item;
    }
  }

  userStore(element: any) {
    this.addUserStore.emit(element);
  }

  surveyMap(element: any) {
    this.addSurveyMap.emit(element);
  }

  hasCustomTemplate(columnId: string): boolean {
    return !!this.customTemplates && !!this.customTemplates[columnId];
  }

  getCustomTemplate(columnId: string): TemplateRef<any> | null {
    return this.customTemplates?.[columnId] || null;
  }

  getApproverList(approver: any): string[] {
    if (!approver) return [];
    if (Array.isArray(approver)) {
      return approver.filter(a => a && a.trim() !== '');
    }
    if (typeof approver === 'string') {
      return approver.split(',').map(a => a.trim()).filter(a => a !== '');
    }
    return [];
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ').filter(p => p);
    if (parts.length >= 2) {
      return (parts[0][0] || '').toUpperCase() + (parts[1][0] || '').toUpperCase();
    }
    return (parts[0]?.[0] || '').toUpperCase() + (parts[0]?.[1] || '').toUpperCase();
  }

  getApproverTooltip(approver: any): string {
    const approverList = this.getApproverList(approver);
    return approverList.length > 0 ? approverList.join(', ') : '';
  }

  getImageSource(element: any, columnId: string): string {
    const value = element[columnId];
    const modalImages: string[] = this.normalizeToArray(value);
    const previewKey = `${columnId}PreviewUrl`;
    const rowCached = element?.[previewKey];
    if (rowCached && typeof rowCached === 'string') {
      return rowCached;
    }

    const first = modalImages[0];
    if (typeof first !== 'string' || !first) {
      return first ?? '';
    }

    const serviceCached = this.blobService.peekCachedReadSasUrl(first);
    if (serviceCached) {
      element[previewKey] = serviceCached;
      return serviceCached;
    }

    if (this.blobService.isAzureBlobUrl(first)) {
      this.ensureBlobImagePreview(element, columnId, first);
      return '';
    }

    return first;
  }

  private prefetchBlobImagePreviews(rows: any[]): void {
    const imageColumnIds = this.displayedColumns
      .map((c) => c.id)
      .filter((id) => TableComponent.BLOB_IMAGE_COLUMN_IDS.has(id));
    if (imageColumnIds.length === 0) {
      return;
    }

    const blobUrls: string[] = [];
    for (const row of rows) {
      for (const columnId of imageColumnIds) {
        const images = this.normalizeToArray(row[columnId]);
        const first = images[0];
        if (typeof first === 'string' && this.blobService.isAzureBlobUrl(first)) {
          const previewKey = `${columnId}PreviewUrl`;
          if (!row[previewKey] && !this.blobService.peekCachedReadSasUrl(first)) {
            blobUrls.push(first);
          }
        }
      }
    }

    if (blobUrls.length === 0) {
      return;
    }

    this.blobService.generateBlobReadSasUrlBatch(blobUrls, 60).subscribe({
      next: (sasByUrl) => {
        for (const row of rows) {
          for (const columnId of imageColumnIds) {
            const images = this.normalizeToArray(row[columnId]);
            const first = images[0];
            if (typeof first !== 'string') {
              continue;
            }
            const sas = sasByUrl.get(first);
            if (sas) {
              row[`${columnId}PreviewUrl`] = sas;
            }
          }
        }
        this.cdr.markForCheck();
      },
    });
  }

  private ensureBlobImagePreview(
    element: any,
    columnId: string,
    blobUrl: string,
  ): void {
    const previewKey = `${columnId}PreviewUrl`;
    if (element[previewKey] || this.pendingBlobPreviewUrls.has(blobUrl)) {
      return;
    }
    this.pendingBlobPreviewUrls.add(blobUrl);
    this.blobService.generateBlobReadSasUrl(blobUrl, 60).subscribe({
      next: (sasUrl: string) => {
        this.pendingBlobPreviewUrls.delete(blobUrl);
        if (sasUrl && typeof sasUrl === 'string') {
          element[previewKey] = sasUrl;
          this.cdr.markForCheck();
        }
      },
      error: () => {
        this.pendingBlobPreviewUrls.delete(blobUrl);
      },
    });
  }
  public handleImageError(event: Event) {
    (event.target as HTMLImageElement).src =
      '/public/store360assets/images/noimage.png'; // Provide path to your default image
  }

  getSerialNumber(index: number): number {
    const currentPage = this.pageIndex || 0;
    const pageSize = this.pageSize || 0;
    return ((currentPage - 1) * pageSize) + index + 1;
  }

  toggleInvoiceRequired(id: number, isInvoiceRequired: boolean) {
    this.isInvoiceRequiredChange.emit({
      id: id,
      isInvoiceRequired: isInvoiceRequired,
    });
  }

  toggleEnterPrice(id: number, isEnterPrice: boolean) {
    this.isEnterPriceChange.emit({
      id: id,
      isEnterPrice: isEnterPrice,
    });
  }

  toggleTest(id: number, isTest: boolean) {
    this.isTestChange.emit({
      id: id,
      isTest: isTest,
    });
  }

  toggleDynamicStatus(id: number, fieldName: string, value: boolean) {
    this.dynamicStatusChange.emit({
      id: id,
      fieldName: fieldName,
      value: value,
    });
  }

  onTargetChange(row: any) {
    this.categoryGroupTargetRow.emit(row);
  }

  public openInNewTab(url: string) {
    const newTab = window.open(url, '_blank');

    if (!newTab) {
      alert('Popup blocked. Please allow popups for this site.');
    }
  }

  public openInvoicePreview(url: string) {
    if (!url) return;

    this.resolveInvoiceUrl(url, (resolvedUrl: string) => {
      if (this.isPdfUrl(url) || this.isPdfUrl(resolvedUrl)) {
        this.downloadInvoicePdf(resolvedUrl, url);
        return;
      }
      if (this.isImageUrl(url) || this.isImageUrl(resolvedUrl)) {
        this.dialog.open(ImagemodalComponent, {
          data: [resolvedUrl],
          maxWidth: '90vw',
          maxHeight: '90vh',
          backdropClass: 'imageModal_backdrop',
          panelClass: ['imageModal', 'image-modals'],
        });
        return;
      }
      this.openInNewTab(resolvedUrl);
    });
  }

  private downloadInvoicePdf(resolvedUrl: string, originalUrl: string): void {
    const base = (originalUrl || resolvedUrl).split('?')[0].split('#')[0];
    const lastSegment = base.split('/').pop() || 'invoice';
    const filename = /\.pdf$/i.test(lastSegment) ? lastSegment : `${lastSegment}.pdf`;

    fetch(resolvedUrl)
      .then((r) => {
        if (!r.ok) throw new Error('fetch failed');
        return r.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        const a = document.createElement('a');
        a.href = resolvedUrl;
        a.download = filename;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
  }

  private extensionFromUrl(url: string): string {
    if (!url) return '';
    const path = url.split('?')[0].split('#')[0];
    const m = path.match(/\.([a-z0-9]+)$/i);
    return m ? m[1].toLowerCase() : '';
  }

  public isImageUrl(url: string): boolean {
    const ext = this.extensionFromUrl(url);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  }

  public isPdfUrl(url: string): boolean {
    return this.extensionFromUrl(url) === 'pdf';
  }

  public getInvoicePreviewUrl(element: any): string {
    const raw = element?.invoiceUrl;
    if (!raw) return '';
    const cached = this.invoiceUrlCache.get(raw);
    if (cached) return cached;
    this.ensureInvoicePreviewUrl(element);
    return '';
  }

  private ensureInvoicePreviewUrl(element: any): void {
    const raw = element?.invoiceUrl;
    if (!raw || typeof raw !== 'string') return;
    this.blobService.generateBlobReadSasUrl(raw, 60).subscribe({
      next: (sasUrl: string) => {
        this.invoiceUrlCache.set(raw, sasUrl);
        element.invoicePreviewUrl = sasUrl;
      },
      error: () => {
      },
    });
  }

  private resolveInvoiceUrl(rawUrl: string, callback: (resolved: string) => void): void {
    const cached = this.invoiceUrlCache.get(rawUrl);
    if (cached) {
      callback(cached);
      return;
    }
    this.blobService.generateBlobReadSasUrl(rawUrl, 60).subscribe({
      next: (sasUrl: string) => {
        this.invoiceUrlCache.set(rawUrl, sasUrl);
        callback(sasUrl);
      },
      error: () => {
        callback(rawUrl);
      },
    });
  }

  normalizeToArray(param: string): string[] {
    try {
      if (typeof param === 'string') {
        try {
          const parsed = JSON.parse(param);
          return Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          // Not JSON, fallback to comma-separated parsing
          return param.includes(',') ? param.split(',').map(s => s.trim()) : [param.trim()];
        }
      }

      // If already an array, return as-is; else wrap it
      return Array.isArray(param) ? param : [param];

    } catch (e) {
      // If JSON.parse fails, treat it as a plain string
      return [param];
    }
  }

  //method for open the image view on modal
  public openModal(images: any, event: Event) {
    if (!images) return;
    let modalImages: string[] = this.normalizeToArray(images);

    event.stopPropagation();
    const resolvedImages: string[] = [];
    let pending = 0;
    let changed = false;
    let opened = false;
    let hasBlobUrls = false;

    const openOnce = (imgs: string[]) => {
      if (opened) return;
      opened = true;
      this.dialog.open(ImagemodalComponent, {
        data: imgs,
        maxWidth: '90vw',
        maxHeight: '90vh',
        backdropClass: 'imageModal_backdrop',
        panelClass: ['imageModal', 'image-modals'],
      }).afterClosed().subscribe(() => { });
    };

    modalImages.forEach((url, idx) => {
      if (typeof url === 'string' && /^https?:\/\/.+\.blob\.core\.windows\.net\//i.test(url)) {
        hasBlobUrls = true;
        pending += 1;
        this.blobService.generateBlobReadSasUrl(url, 60).subscribe({
          next: (sasUrl: string) => {
            resolvedImages[idx] = sasUrl || url;
            changed = changed || (!!sasUrl && sasUrl !== url);
            pending -= 1;
            if (pending === 0) {
              openOnce(changed ? resolvedImages : modalImages);
            }
          },
          error: () => {
            resolvedImages[idx] = url;
            pending -= 1;
            if (pending === 0) {
              openOnce(changed ? resolvedImages : modalImages);
            }
          },
        });
      } else {
        resolvedImages[idx] = url;
      }
    });

    // Only open immediately when there are no blob URLs to resolve.
    if (!hasBlobUrls) {
      openOnce(modalImages);
    }
  }
  //method for close image view mopdal
  public closeModal() {
    this.modalImages = [];
  }

  isLevelFourGroupRole(roleId: number | string | number[]): boolean {
    const roleIds = Array.isArray(roleId) ? roleId : [roleId];
    return roleIds.some((id) =>
      this.levelThreeGroupRoles.includes(Number(id)),
    );
  }

  showInteluxUserActions(element: any): boolean {
    return element?.isInteluxUser === true;
  }

  resetPasswordUser(element: any) {
    this.resetPassword.emit(element);
  }

  resetStoreLocationBtn(element: any) {
    this.resetStoreLocation.emit(element);
    console.log(element);
  }

  openTrainingQrCode(url: string, title?: string): void {
    if (!url) {
      return;
    }

    this.dialog.open(QrCodeDialogComponent, {
      data: { url, title: title || 'Join Training QR Code' },
      width: '400px',
      maxWidth: '95vw',
      panelClass: ['qr-code-dialog-panel'],
      backdropClass: 'imageModal_backdrop',
    });
  }

  async copyTrainingQrCode(url: string, element?: any): Promise<void> {
    if (!url || this.isCopyDisabled(element)) {
      return;
    }

    try {
      const dataUrl = await generateQrDataUrl(url);
      await copyQrImageToClipboard(dataUrl);
      this.toastr.success('QR code copied to clipboard!', 'Success', {
        timeOut: 2000,
        positionClass: 'toast-top-right',
      });
    } catch {
      this.toastr.error('Failed to copy QR code. Please try again.', 'Error');
    }
  }

  copyUrl(url: string, element?: any): void {
    if (!url) {
      return;
    }

    // Check if Clipboard API is available (requires secure context)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Use modern Clipboard API
      navigator.clipboard.writeText(url).then(() => {
        this.showCopySuccess(element);
      }).catch(err => {
        if (url.startsWith('http')) {
          console.error('Failed to copy URL using Clipboard API:', err);
        } else {
          console.error('Failed to copy batch id:', err);
        }
        // Fallback to legacy method if Clipboard API fails
        this.fallbackCopyTextToClipboard(url, element);
      });
    } else {
      // Fallback to legacy method for non-secure contexts
      this.fallbackCopyTextToClipboard(url, element);
    }
  }

  private fallbackCopyTextToClipboard(text: string, element?: any): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        this.showCopySuccess(element, text);
      } else {
        console.error('Fallback: Copy command was unsuccessful');
        this.toastr.error('Failed to copy URL. Please copy manually.', 'Error');
      }
    } catch (err) {
      console.error('Fallback: Unable to copy', err);
      this.toastr.error('Failed to copy URL. Please copy manually.', 'Error');
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private showCopySuccess(element: any, text?: any): void {
    if (text.startsWith('http')) {
      this.toastr.success('URL copied to clipboard!', 'Success', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    } else {
      this.toastr.success('Batch ID copied to clipboard!', 'Success', {
        timeOut: 2000,
        positionClass: 'toast-top-right'
      });
    }

    // Visual feedback - change icon color temporarily
    if (element) {
      element.copied = true;
      setTimeout(() => {
        element.copied = false;
      }, 2000);
    }
  }

  /**
   * Resolves post-test quiz end instant for comparison. Prefers raw API value when present.
   */
  private getPostTestQuizEndMs(element: any): number | null {
    const at = element?.postTestQuizEndAt;
    if (at != null && at !== '') {
      const t = new Date(at).getTime();
      return Number.isNaN(t) ? null : t;
    }
    const display = element?.['post-test-quiz-end'];
    if (
      display == null ||
      display === '' ||
      display === '--' ||
      display === 'N/A'
    ) {
      return null;
    }
    const t = new Date(display).getTime();
    return Number.isNaN(t) ? null : t;
  }

  /**
   * Parses "YYYY-MM-DD HH:mm" or "YYYY-MM-DD HH:mm:ss" as a local Date.
   * This avoids browser inconsistencies with `new Date("YYYY-MM-DD HH:mm:ss")`.
   */
  private parseLocalYmdTimeToMs(value: any): number | null {
    if (value == null) return null;
    const s = String(value).trim();
    if (!s) return null;

    // If it's already ISO-like, Date can handle it.
    if (s.includes('T')) {
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? null : t;
    }

    // Expected: "YYYY-MM-DD HH:mm[:ss]"
    const [datePart, timePart] = s.split(' ');
    if (!datePart || !timePart) {
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? null : t;
    }

    const d = datePart.split('-').map(n => Number(n));
    const tt = timePart.split(':').map(n => Number(n));
    if (d.length !== 3 || tt.length < 2) return null;

    const [y, mo, da] = d;
    const [h, mi, se = 0] = tt;
    if (
      !Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da) ||
      !Number.isFinite(h) || !Number.isFinite(mi) || !Number.isFinite(se)
    ) {
      return null;
    }

    const dt = new Date(y, mo - 1, da, h, mi, se, 0);
    const ms = dt.getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  /**
   * Resolves quiz start instant (date + time) for scheduled live quiz gating.
   * Accepts raw API `startDateTime` when present; falls back to common display fields.
   */
  private getStartDateTimeMs(element: any): number | null {
    const raw = element?.startDateTime ?? element?.['start-date-time'] ?? element?.start_date_time;
    if (raw != null && raw !== '') {
      return this.parseLocalYmdTimeToMs(raw);
    }

    const startDate = element?.startDate ?? element?.['start-date'] ?? element?.trainingDate ?? element?.['trainingDate'];
    const startTime = element?.startTime ?? element?.['start-time'];
    if (startDate != null && startDate !== '' && startTime != null && startTime !== '') {
      return this.parseLocalYmdTimeToMs(`${startDate} ${startTime}`);
    }

    return null;
  }

  isCopyDisabled(element: any): boolean {
    const status: any =
      element?.progress ||
      element?.['activity-status'] ||
      element?.status ||
      element?.['status-text'];
    if (typeof status !== 'string') {
      return false;
    }
    const st = status.toLowerCase();
    if (st === 'scheduled') {
      const quizTypeId = Number(element?.quizTypeId);
      if (quizTypeId !== 3) {
        return true;
      }
      const startMs = this.getStartDateTimeMs(element);
      if (startMs == null) {
        // Preserve legacy behaviour when API doesn't provide start datetime.
        return false;
      }
      // Disable until quiz start date+time is reached.
      return Date.now() < startMs;
    }
    if (st === 'completed') {
      if (
        element == null ||
        !Object.prototype.hasOwnProperty.call(element, 'post-test-quiz-end')
      ) {
        return true;
      }
      const endMs = this.getPostTestQuizEndMs(element);
      if (endMs == null) {
        return true;
      }
      return Date.now() > endMs;
    }
    return st === 'cancelled' || st === 'expired';
  }

  isCancelActionVisible(element: any): boolean {
    if (element?.hideFieldAction) {
      return false;
    }

    const status: any =
      element?.['status-text'] ||
      element?.status ||
      element?.progress ||
      element?.['activity-status'];

    if (!status) {
      return false;
    }

    const st = status.toString().toLowerCase();
    return st === 'scheduled';
  }


  getScoreClass(score: number): string {
    if (score >= 75) return 'score-green';
    if (score >= 40 && score < 75) return 'score-orange';
    if (score > 0 && score < 40) return 'score-red';
    return '';
  }


  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip-gray';

    const statusLower = status.toLowerCase();

    switch (statusLower) {
      case 'scheduled':
      case 'planned':
        return 'status-chip-scheduled';
      case 'in progress':
      case 'progress':
      case 'unplanned':
        return 'status-chip-progress';
      case 'completed':
        return 'status-chip-completed';
      case 'cancelled':
      case 'not visited':
        return 'status-chip-cancelled';
      default:
        return 'status-chip-gray';
    }
  }

  getAttendanceStatusClass(status: unknown): string {
    if (typeof status !== 'string') {
      return '';
    }

    switch (status.trim().toLowerCase()) {
      case 'yes':
        return 'attendance-status-yes';
      case 'no':
        return 'attendance-status-no';
      default:
        return '';
    }
  }

  cancelTraining(id: number) {
    this.cancelTrainingList.emit(id);
  }

  viewAegSurvey(element: any) {
    this.aegSurveyView.emit(element);
  }




  // Generate consistent color based on name
  getColorForName(name: string | null | undefined): string {
    const colors = ['#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#e67e22', '#e74c3c', '#16a085', '#27ae60', '#2980b9', '#8e44ad'];
    if (!name) return '#999';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  }


  viewSnag(element: any) {
    this.snagView.emit(element);
  }
  resolveSnag(element: any) {
    this.snagResolve.emit(element);
  }
  reopenSnag(element: any) {
    this.snagReopen.emit(element);
  }

  isClampedListColumn(columnId: string): boolean {
    return TableComponent.CLAMPED_LIST_COLUMN_IDS.has(columnId);
  }

  getCellListText(value: unknown): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const text = String(value).trim();
    if (!text || text === '--') {
      return null;
    }
    return text;
  }

  shouldShowMoreForCell(value: unknown): boolean {
    const text = this.getCellListText(value);
    if (!text) {
      return false;
    }
    const commaCount = (text.match(/,/g) ?? []).length;
    return text.length > 72 || commaCount >= 2;
  }

  openCellMoreTooltip(tooltip: MatTooltip, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    tooltip.show();
  }
}
